import React, { useState } from 'react';
import { Typography } from '@mui/material';
import { Button, Fieldset, Flex, Grid, GridArea, Heading } from '../../components';
import { css } from '@emotion/css';
import { EnrollmentManager } from '../../components';
import { useMutation } from '@apollo/client';
import gql from 'graphql-tag';
import { useToast } from '../../hooks';

interface Option {
  label: string;
  value: string;
}

const gridTemplate = `
"included excluded"
"format run"
`;

const CREATE_PREAUTH_LINK = gql`
  mutation CreatePreAuthReportLink($format: String!) {
    url: preAuthReportUrl(format: $format)
  }
`;

function ReportFilterPage() {
  const toaster = useToast();
  const [includedData, setIncludedData] = useState<Option[]>([]);
  const [excludedData, setExcludedData] = useState<Option[]>([]);
  const [format, setFormat] = useState('csv');

  const [createPreAuthLink, { loading }] = useMutation(CREATE_PREAUTH_LINK);

  const handleDataChange =
    (setter: (data: Option[]) => void, otherList: Option[]) => (data: Option[]) => {
      const finalData = data.filter(
        item => !otherList.some(otherItem => otherItem.value === item.value)
      );
      setter(finalData);
    };

  const handleFormatChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setFormat(e.target.value);
  };

  const handleGenerateReport = async (e: React.FormEvent) => {
    e.preventDefault();

    if (includedData.length === 0) {
      toaster('Please include at least one item in the Included list', { context: 'warning' });
      return;
    }

    try {
      const { data } = await createPreAuthLink({
        variables: { format },
      });

      if (data?.url) {
        toaster('Pre-auth link generated successfully');
        console.log(`This is the data for the preAuthUrl: ${data.url}`);
        await handleDownloadReport(data.url);
      } else {
        throw new Error('Failed to generate the pre-auth link');
      }
    } catch (e) {
      console.error('Error generating report:', e);
      toaster(`Failed to generate the report. ${e}`, {
        context: 'danger',
      });
    }
  };

  const handleDownloadReport = async (url: string) => {
    const payload = {
      Including: includedData.map(option => option.value),
      Excluding: excludedData.map(option => option.value),
    };
    try {
      if (url) {
        const fullUrl = `${process.env.REACT_APP_GRAPH_HOST}${url}`;

        console.log(`Data for fetch request: Url: ${fullUrl}, Params: ${payload}`);

        const apiResponse = await fetch(fullUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        });

        if (apiResponse.ok) {
          // Create downloadable link
          const blob = await apiResponse.blob();
          const link = document.createElement('a');
          link.href = window.URL.createObjectURL(blob);
          link.download = `report.${format}`;
          document.body.appendChild(link);
          link.click();
          link.remove();
        } else {
          const errorText = await apiResponse.text();
          throw new Error(`API Error: ${errorText}`);
        }
      } else {
        throw new Error('Pre-auth link is not set');
      }
    } catch (e) {
      console.error('Error downloading report:', e);
      toaster(`Failed to download the report. ${e}`, { context: 'danger' });
    }
  };

  return (
    <form onSubmit={handleGenerateReport}>
      <Heading>Reports</Heading>
      <Fieldset>
        <Grid
          gap={26}
          className={css({
            display: 'grid',
            gridTemplateAreas: gridTemplate,
            gridTemplateColumns: '1fr 1fr',
            gridTemplateRows: 'auto auto',
          })}
        >
          <GridArea area="included">
            <Typography variant="h6">Included</Typography>
            <EnrollmentManager
              onDataChange={handleDataChange(setIncludedData, excludedData)}
              currentList={includedData}
              otherList={excludedData}
            />
          </GridArea>
          <GridArea area="excluded">
            <Typography variant="h6">Excluded</Typography>
            <EnrollmentManager
              onDataChange={handleDataChange(setExcludedData, includedData)}
              currentList={excludedData}
              otherList={includedData}
            />
          </GridArea>
          <GridArea area="format">
            <Typography variant="h6">Format</Typography>
            <select value={format} onChange={handleFormatChange}>
              <option value="csv">CSV</option>
              <option value="xlsx">Excel</option>
            </select>
          </GridArea>
          <GridArea area="run" className={css({ justifySelf: 'right' })}>
            <Flex>
              <Button type="submit" disabled={loading}>
                Generate
              </Button>
            </Flex>
          </GridArea>
        </Grid>
      </Fieldset>
    </form>
  );
}

export default ReportFilterPage;
